import axios from 'axios';
import { tokenManager } from '../contexts/AuthContext';



const currentHost = window.location.hostname; 

// 使用相對路徑 /api 來觸發 Firebase Hosting 的 Rewrite 規則
// 這樣可以隱藏後端 Cloud Run 的原始網址，並解決跨域問題
var API_BASE_URL = '/api';

if (currentHost === 'localhost' || currentHost === '127.0.0.1') {
    // 如果是在電腦本地開發，則連向本地後端
    console.log("API: Running on localhost, connecting to local backend");
    API_BASE_URL = 'http://localhost:8040/api';
} else if (/^\d+\.\d+\.\d+\.\d+$/.test(currentHost)) {
    // 如果是用 IP 訪問（例如區域網測試 192.168.x.x），連向該 IP 的 8040 埠
    console.log("API: IP access detected, connecting to backend on port 8040");
    API_BASE_URL = `http://${currentHost}:8040/api`;
} else {
    // 生產環境或託管環境，使用相對路徑 /api
    console.log("API: Hosted environment, using relative path /api (Firebase Rewrite)");
    API_BASE_URL = '/api';
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = tokenManager.getToken();
    
    console.log("🔍 [Interceptor] URL:", config.url);
    console.log("🔍 [Interceptor] Token found:", token ? "YES (Length: " + token.length + ")" : "NO");
    // 只要有 Token 就帶上，讓 Backend 判斷是否過期
    // 這樣可以避免前端時間不準導致的誤判
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle authentication errors and token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        console.warn("⚠️ Token expired or unauthorized.");

        try {
          // Try to refresh the token
          const refreshResponse = await refreshAuthToken();

          if (refreshResponse.success) {
            // Update the authorization header and retry the request
            originalRequest.headers.Authorization = `Bearer ${refreshResponse.token}`;
            return api(originalRequest);
          } else {
            // Refresh failed, redirect to login
            handleAuthFailure();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          // Refresh failed, redirect to login
          handleAuthFailure();
          return Promise.reject(error);
        }
      } else {
        // 已經 refresh 過再 retry,個 request 仲係 401——SimpleJWT refresh 淨係
        // 驗簽名/過期,唔會查返 DB 個 user 仲喺唔喺度,所以一個對應住已刪除/
        // 唔存在用戶嘅 refresh token 都可以「成功」refresh 出一個新、但一樣冧用
        // 得嘅 access token,搞到之前呢度冧做嘢,個死 session 會一直卡住唔清。
        // 呢種情況一定係個 session 本身有問題(唔係普通嘅 token 啱啱過期),
        // 直接清晒佢逼返去登入,唔好靜靜雞 reject 完就算。
        console.warn("⚠️ Retried request still 401 after refresh — session invalid, forcing logout.");
        handleAuthFailure();
        return Promise.reject(error);
      }
    }

    // Handle other authentication errors
    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data);
      // You might want to show a user-friendly message here
    }

    return Promise.reject(error);
  }
);

// Token refresh function
export const refreshAuthToken = async () => {
  try {
    const refreshToken = tokenManager.getRefreshToken();
    // 獲取當前用戶身分
    const authType = tokenManager.getAuthType();

    if (!refreshToken) {
      // 如果沒有 refresh token，嘗試使用 access token（向後兼容）
      const accessToken = tokenManager.getToken();
      if (!accessToken) {
        return { success: false, error: 'No tokens available' };
      }
    }

    // 建立一個乾淨的 axios instance 避免無窮迴圈
    const refreshApi = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

// 🌟 修改 1: 兼容 SimpleJWT 的 Payload Key 'refresh'
    // 如果你舊系統一定要用 'refresh_token'，請檢查你的 Backend Serializer
    // 這裡預設改為 'refresh' (SimpleJWT 標準)
    const requestData = refreshToken 
    ? { refresh: refreshToken } // 改成 refresh (SimpleJWT 預設)
    : {};
    

// 🌟 修改 2: 統一 Refresh Endpoint
    // 建議在 Backend urls.py 將 /ssms/token/refresh/ 和 /auth/refresh/ 指向同一個 View
    // 這裡我們先打 SSMS 確定的路徑，或者你可以保留舊路徑 '/auth/refresh/'

// 🌟 修改重點: 動態決定 Refresh Endpoint 🌟
    let refreshUrl = '/auth/token/refresh/'; // 預設: 顧客/LINE Login 用既 Endpoint (請確認你 Backend users app 既 URL)
    
    if (authType === 'staff') {
        refreshUrl = '/ssms/token/refresh/'; // 員工專用 Endpoint
    } 

    console.log(`正在刷新 Token... 身分: ${authType}, URL: ${refreshUrl}`); // Debug 用
    
    const response = await refreshApi.post(refreshUrl, requestData);

// 🌟 修改 3: 兼容回傳欄位 (access vs access_token)
    const data = response.data;
    const newToken = data.access || data.access_token;
    const newRefreshToken = data.refresh || data.refresh_token;

    if (newToken) {
      if (newRefreshToken) {
        tokenManager.setTokens(newToken, newRefreshToken);
      } else {
        // 如果 Backend 只回傳 Access Token，就只更新 Access
        tokenManager.setToken(newToken);
      }
      
      window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
        detail: { token: newToken } 
      }));
      
      return { success: true, token: newToken };
    } else {
      return { success: false, error: 'No token in refresh response' };
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    return { success: false, error: error.message };
  }
};

// Handle authentication failure
const handleAuthFailure = () => {
  // 1. Clear stored authentication data
  console.warn("⚠️ [Interceptor][handleAuthFailure] 收到 401 Unauthorized");
  tokenManager.removeToken();
  
  // 2. Dispatch custom event (通知 AuthContext)
  window.dispatchEvent(new CustomEvent('authFailure'));
  
  const currentPath = window.location.pathname;
  const fullPath = currentPath + window.location.search;

  // 3. 判斷是用戶端還是員工端
  const isSsms = currentPath.startsWith('/ssms');
  const targetLoginUrl = isSsms ? '/ssms/login' : '/login';

  // 4. 防止無限 Loop (如果已經在登入頁，就不用再 Redirect)
  // 注意：這裡同時檢查了 SSMS 和 客人端的登入頁
  if (currentPath !== targetLoginUrl && currentPath !== '/auth/callback') {
    console.warn("⚠️ [Interceptor][handleAuthFailure] 防止無限 Loop");
    // 記住使用者原本想去哪
    // 5. Store current location (保留你原本的優點！)
    sessionStorage.setItem('auth_redirect', currentPath + window.location.search);
    // 跳轉回登入頁
    window.location.href = `${targetLoginUrl}?error=session_expired`;
  }
};

// Services API
export const getServices = (merchantSlug) => {
  if (merchantSlug) {
    // 正確的商戶服務端點
    return api.get(`/merchants/${merchantSlug}/services/`);
  }
  // 全域服務端點（向後兼容）
  return api.get('/services/');
};

// Get User Coupon Api
export const getUserCoupons = () => {
  /*if (merchantSlug) {
    return api.get(`/merchants/${merchantSlug}/coupons/`);
  }
    */
  // 如果無 slug，就打返舊路徑 (或者報錯)
  return api.get('/users/coupons/');
};

// update user name by user
export const updateProfile = (data) => api.patch('/users/profile/update/', data);

// 個人資料頁「加/改電話」——同註冊嗰條 OTP 共用 request,verify 就用專屬 endpoint
// (update_user_profile 個 generic PATCH 已經唔准改 phone_number)。
export const requestPhoneOtp = (phone_number) => api.post('/auth/sms/request/', { phone_number });
export const updatePhoneWithOtp = (data) => api.post('/users/profile/phone/', data);

// Merchant API
export const getMerchant = (slug) => {
  console.log('API - getMerchant called with slug:', slug);
  const url = `/merchants/${slug}/`;
  console.log('API - calling URL:', url);
  return api.get(url);
};

export const checkTenant = (domain) => {
  return api.get(`/merchants/check-tenant/?domain=${domain}`);
};

export const getActivePromotion = (slug) => {
  return api.get(`/merchants/${slug}/active-promotion/`);
};

// Bookings API
export const createBooking = (bookingData, merchantSlug) => {
  if (merchantSlug) {
    return api.post(`/merchants/${merchantSlug}/bookings/`, bookingData);
  }
  return api.post('/bookings/', bookingData);
};

export const createAnonymousBooking = (bookingData, merchantSlug) => {
  if (merchantSlug) {
    return api.post(`/merchants/${merchantSlug}/bookings/anonymous/`, bookingData);
  }
  return api.post('/bookings/anonymous/', bookingData);
};

export const getBookings = (params = {}, merchantSlug) => {
  if (merchantSlug) {
    return api.get(`/merchants/${merchantSlug}/bookings/`, { params });
  }
  return api.get('/bookings/', { params });
};
export const getBooking = (id, merchantSlug) => {
  if (merchantSlug) {
    return api.get(`/merchants/${merchantSlug}/bookings/${id}/`);
  }
  return api.get(`/bookings/${id}/`);
};
export const confirmBooking = (id, merchantSlug) => {
  if (merchantSlug) {
    return api.post(`/merchants/${merchantSlug}/bookings/${id}/confirm/`);
  }
  return api.post(`/bookings/${id}/confirm/`);
};
export const cancelBooking = (id, merchantSlug) => {
  if (merchantSlug) {
    return api.post(`/merchants/${merchantSlug}/bookings/${id}/cancel/`);
  }
  return api.post(`/bookings/${id}/cancel/`);
};
export const completeBooking = (id, merchantSlug) => {
  if (merchantSlug) {
    return api.post(`/merchants/${merchantSlug}/bookings/${id}/complete/`);
  }
  return api.post(`/bookings/${id}/complete/`);
};



// Additional booking endpoints
export const getTodayBookings = () => api.get('/bookings/bookings/today/');
export const getUpcomingBookings = () => api.get('/bookings/bookings/upcoming/');
export const getBookingStats = () => api.get('/bookings/bookings/stats/');

// Filtered bookings
export const getBookingsByStatus = (status) => api.get('/bookings/bookings/', { params: { status } });
export const getBookingsByService = (serviceId) => api.get('/bookings/bookings/', { params: { service: serviceId } });
export const getBookingsByDateRange = (dateFrom, dateTo) => api.get('/bookings/bookings/', { 
  params: { date_from: dateFrom, date_to: dateTo } 
});

// Search bookings
export const searchBookings = (query) => api.get('/bookings/bookings/', { params: { search: query } });

// Payments API
export const createPayment = (bookingId) => api.post(`/payments/create/${bookingId}/`);
export const getPaymentStatus = (paymentId) => api.get(`/payments/status/${paymentId}/`);
export const refundPayment = (paymentId, refundAmount) => api.post(`/payments/refund/${paymentId}/`, { refund_amount: refundAmount });

// Events (活動報名 + 綠界收款) API
export const getEvent = (slug) => api.get(`/events/${slug}/`);
export const registerForEvent = (slug, payload) => api.post(`/events/${slug}/register/`, payload);
export const getRegistrationStatus = (registrationId) => api.get(`/registrations/${registrationId}/status/`);

//ssms API
export const ssmsLogin = (username, password) => api.post('/ssms/login/', { username, password });

// 👷 員工操作
// 讀取/寫入 自己的報更 (ShiftRequest)
export const getMyShiftRequests = (params) => api.get('/ssms/staff/request/', { params });
export const saveMyShiftRequests = (payload) => api.post('/ssms/staff/request/', payload);
// 員工讀取 更表
export const getMyRoster = (params = {}) => api.get('/ssms/staff/roster/', { params });

export const getAllRoster = (params = {}) => api.get('/ssms/staff/roster/', { 
    params: { ...params, view: 'all' } 
});

// 👔 店長操作
// 讀取 所有員工的報更 (ShiftRequest) - 作為參考用
export const getManagerShiftRequests = (params) => api.get('/ssms/manager/request/', { params });
// Manager Actions
// page_size 頂到 500:backend 預設 PAGE_SIZE=20,冇呢個 param 會靜靜哋淨係攞到
// 第一頁,排更畫面就會少咗 20 個之後嘅同仁揀(同 getProducts 果個 bug 一樣)。
export const getManagerStaffList = () => api.get('/ssms/manager/staff/?page_size=500');
// 支援 filter params (start, end)
export const getManagerRoster = (params = {}) => api.get('/ssms/manager/roster/', { params });
// Batch Save
export const saveManagerRosterBatch = (payload) => api.post('/ssms/manager/roster/batch/', payload);

export default api;